import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { IAdminManager } from '@apis/adminManagers';
import { TradePill } from '@components/ui/TradePill/TradePill';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { PencilIcon, SortIcon, TrashIcon } from '@/utils/icons';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { roleLabel } from './roleLabel';
import table from '../../adminTable.module.css';
import styles from './ManagersTable.module.css';

type SortKey = 'role' | 'name' | 'phone';
type SortDir = 'asc' | 'desc';

// Columns: Role, Full Name, Phone Number, Display (the manager's colored
// chip); the chip column is not sortable.
const SORTABLE_COLUMNS: { key: SortKey; labelKey: string }[] = [
  { key: 'role', labelKey: 'adminManagement.col.role' },
  { key: 'name', labelKey: 'adminManagement.col.fullName' },
  { key: 'phone', labelKey: 'adminManagement.col.phone' },
];

function compare(a: IAdminManager, b: IAdminManager, key: SortKey, t: TFunction): number {
  switch (key) {
    case 'role':
      return roleLabel(t, a).localeCompare(roleLabel(t, b));
    case 'name':
      return a.name.localeCompare(b.name);
    case 'phone':
      return (a.phoneNumber ?? '').localeCompare(b.phoneNumber ?? '');
  }
}

interface IManagersTableProps {
  managers: IAdminManager[];
  emptyMessage: string;
  onEdit: (manager: IAdminManager) => void;
  onDelete: (manager: IAdminManager) => void;
}

export function ManagersTable({ managers, emptyMessage, onEdit, onDelete }: IManagersTableProps) {
  const { t } = useTranslation();
  // null = the server's default order (trade sort index, then name); a
  // column sort applies only once clicked.
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sorted = useMemo(() => {
    if (!sortKey) return managers;
    const copy = [...managers];
    copy.sort((a, b) => {
      const c = compare(a, b, sortKey, t);
      return sortDir === 'asc' ? c : -c;
    });
    return copy;
  }, [managers, sortKey, sortDir, t]);

  return (
    <ScrollArea orientation="both" className={table.tableWrap}>
      <table className={table.table}>
        <thead>
          <tr className={table.headerRow}>
            {SORTABLE_COLUMNS.map((col) => (
              <th key={col.key} className={table.th}>
                <button type="button" className={table.sortBtn} onClick={() => handleSort(col.key)}>
                  {t(col.labelKey)}
                  <SortIcon />
                </button>
              </th>
            ))}
            <th className={table.th}>{t('adminManagement.col.display')}</th>
            <th className={`${table.th} ${table.thCenter}`}>{t('adminManagement.col.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td className={table.empty} colSpan={SORTABLE_COLUMNS.length + 2}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sorted.map((manager) => (
              <tr key={manager.id} className={table.row}>
                <td className={table.td}>{roleLabel(t, manager)}</td>
                <td className={table.td}>{manager.name}</td>
                <td className={manager.phoneNumber ? table.td : `${table.td} ${table.muted}`}>
                  {manager.phoneNumber ?? '—'}
                </td>
                <td className={table.td}>
                  <TradePill
                    label={manager.name}
                    {...(manager.backgroundColor
                      ? { backgroundColor: manager.backgroundColor }
                      : {})}
                  />
                </td>
                <td className={table.tdCenter}>
                  <span className={styles.actionsCell}>
                    <Tooltip content={t('adminManagement.actions.edit')}>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        aria-label={t('adminManagement.actions.edit')}
                        onClick={() => onEdit(manager)}
                      >
                        <PencilIcon size={15} />
                      </button>
                    </Tooltip>
                    <Tooltip content={t('adminManagement.actions.delete')}>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        aria-label={t('adminManagement.actions.delete')}
                        onClick={() => onDelete(manager)}
                      >
                        <TrashIcon size={15} />
                      </button>
                    </Tooltip>
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </ScrollArea>
  );
}